import React, { useState } from 'react'
import {
  Box,
  Button,
  Grid,
  ThemeProvider,
  Typography,
  createTheme,
  alpha,
} from '@mui/material'
import {
  VenueSustainableInitiativesSustainabilityInitiativeFragment,
  VenueSustainableInitiativesVenueFragment,
  useVenueSustainableInitiativesGetSustainabilityInitiativesForVenueQuery,
  useVenueSustainableInitiativesGetSustainabilityInitiativesQuery,
} from './VenueSustainableInitiatives.generated-types'
import { ORDER_OF_SUSTAINABILITY_INITIATIVES } from '../../Constants'
import { Dialog } from './Dialog'
import { captureException, withScope } from '@sentry/react'
import { CombinedError } from 'urql'
import { sustainabilityInitiativeCategoriesToIcons } from './Icons'
import { NoSustainabilityInitiativesIcon } from './Icons/NoSustainabilityInitiativesIcon'

interface VenueSustainableInitiativesProps {
  venue: VenueSustainableInitiativesVenueFragment | null
}

const HEADER_TEXT: string = "What this venue's doing"

// TODO: remove this when MUI is used globally throughout the application and set in the theme instead
const GREY: string = '#1F1F1F'

// TODO: remove this when MUI is used globally throughout the application and set in the theme instead
const TYPOGRAPHY_LINE_HEIGHT: string = '24px'

// TODO: remove this when MUI is used globally throughout the application
const THEME = createTheme({
  typography: {
    allVariants: {
      color: '#1F1F1F',
      fontFamily: ['Montserrat', 'Arial', 'sans-serif'].join(','),
      lineHeight: TYPOGRAPHY_LINE_HEIGHT,
      letterSpacing: 'normal',
    },
    h3: {
      fontSize: '18px',
    },
    button: {
      textTransform: 'none',
    },
  },
})

const logQueryErrorToSentry = (
  error: CombinedError,
  formattedQueryName: string
): void => {
  withScope((scope) => {
    scope.setTransactionName(
      `[VenueSustainableInitiatives] ${formattedQueryName} query failed to load`
    )
    scope.setContext('GraphQL context', {
      'Urql CombinedError': error,
      'Error message': error.message,
      'GraphQL errors': error.graphQLErrors,
      'Network error': error.networkError,
    })
  })

  captureException(error)
}

const HeaderTypography = (): JSX.Element => (
  <Typography
    variant="h3"
    sx={{
      fontWeight: 600,
      marginBottom: '16px',
    }}
  >
    {HEADER_TEXT}
  </Typography>
)

const renderContentToDisplay = (
  error: boolean,
  sustainabilityInitiatives:
  | VenueSustainableInitiativesSustainabilityInitiativeFragment[]
  | undefined,
  allSustainabilityInitiatives:
  | VenueSustainableInitiativesSustainabilityInitiativeFragment[]
  | undefined,
  [dialogOpen, setDialogOpen]: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>
  ]
): JSX.Element => {
  if (error || !sustainabilityInitiatives || !allSustainabilityInitiatives) {
    return (
      <Box
        sx={{
          margin: '48px 0px',
        }}
      >
        <HeaderTypography />
        <Typography>
          Something went wrong whilst loading the Sustainable Initiatives for
          this venue.
        </Typography>
      </Box>
    )
  }

  // Only the top six sustainability initatives should be displayed - they are already sorted by the "get Sustainability Initiatives for Venue" query
  const topSixSustainabilityInitiatives = sustainabilityInitiatives.slice(0, 6)

  return (
    <Box
      sx={{
        margin: '48px 0px',
      }}
    >
      <Box display="flex" sx={{ alignItems: 'center', marginBottom: '16px' }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {HEADER_TEXT}
        </Typography>
        {/*
           * TODO: remove this when MUI is used globally throughout the application and use module augmentation instead

           * This theming with sx is done to match the button with how MUI would calculate the border and hover colours when using
           * a custom color - see https://github.com/mui/material-ui/blob/v5.0.0-alpha.37/packages/material-ui/src/Button/Button.js#L89
           * for how this is originally done by MUI. See https://mui.com/material-ui/customization/default-theme/ and specifically palette.action.hoverOpacity
           * for the source of the opacity when setting the hover colour. This overriding should be done using an overriden
           * theme and module augmentation but since MUI isn't used globally yet this override ensures no existing components will break.
           */}
        <Button
          variant="outlined"
          sx={{
            color: GREY,
            border: `1px solid ${GREY}`,
            '&:hover': {
              backgroundColor: alpha(GREY, 0.04),
              border: `1px solid ${GREY}`,
            },
            fontWeight: 600,
            marginLeft: '16px',
            flexShrink: 0,
          }}
          onClick={() => setDialogOpen(true)}
        >
          See{' '}
          {sustainabilityInitiatives.length > 6
            ? `${sustainabilityInitiatives.length} venue`
            : 'all'}{' '}
          initiatives
        </Button>
        <Dialog
          title={HEADER_TEXT}
          allSustainabilityInitiatives={allSustainabilityInitiatives}
          selectedSustainabilityInitiatives={sustainabilityInitiatives}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      </Box>
      <Grid container spacing={2} direction="row">
        {topSixSustainabilityInitiatives.length ? (
          topSixSustainabilityInitiatives.map((sustainabilityInitiative) => (
            <Grid
              key={sustainabilityInitiative.id}
              xs={4}
              display="inline-flex"
              item
            >
              {
                // This box is used to line up the icon with the text so it always is aligned in the middle of the top line of text, even if the text overflows onto multiple lines
              }
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  height: TYPOGRAPHY_LINE_HEIGHT,
                  color: '#1F1F1F',
                }}
              >
                {
                  sustainabilityInitiativeCategoriesToIcons[
                    sustainabilityInitiative.category
                  ]
                }
              </Box>
              <Typography component="span" sx={{ marginLeft: '8px' }}>
                {sustainabilityInitiative.label}
              </Typography>
            </Grid>
          ))
        ) : (
          <Grid display="inline-flex" xs item>
            <Box
              display="flex"
              justifyContent="center"
              flexGrow={1}
              alignItems="center"
            >
              <NoSustainabilityInitiativesIcon />
              <Typography
                // 250px ensures the text (always) wraps to the next line at the correct position
                sx={{
                  maxWidth: '250px',
                  fontWeight: 500,
                  marginLeft: '16px',
                }}
              >
                This venue hasn’t given us any sustainability information yet
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export const VenueSustainableInitiatives = ({
  venue,
}: VenueSustainableInitiativesProps): JSX.Element | null => {
  const dialogState = useState<boolean>(false)

  /*
   * TODO: remove this when the venue ID is made non-nullable
   * The venue ID shouldn't be nullable but since it is, it is better to not show anything rather than crashing the application
   * if it is null.
   */
  const queryVariables = venue?.ID
    ? {
      input: {
        venueId: venue.ID,
        orderOfSustainabilityInitatives: ORDER_OF_SUSTAINABILITY_INITIATIVES,
      },
    }
    : undefined

  const [
    {
      fetching,
      error: getSustainabilityInitiativesForVenueQueryError,
      data: getSustainabilityInitiativesForVenueQueryData,
    },
  ] = useVenueSustainableInitiativesGetSustainabilityInitiativesForVenueQuery({
    variables: queryVariables,
    pause: !queryVariables,
  })

  const [
    {
      fetching:
        fetchingVenueSustainableInitiativesGetSustainabilityInitiativesQuery,
      error: sustainableInitiativesGetSustainabilityInitiativesQueryError,
      data: sustainableInitiativesGetSustainabilityInitiativesQueryData,
    },
  ] = useVenueSustainableInitiativesGetSustainabilityInitiativesQuery()

  if (
    fetching ||
    fetchingVenueSustainableInitiativesGetSustainabilityInitiativesQuery
  ) {
    return null
  }

  if (getSustainabilityInitiativesForVenueQueryError) {
    logQueryErrorToSentry(
      getSustainabilityInitiativesForVenueQueryError,
      'get Sustainability Initiatives For Venue'
    )
  }

  if (sustainableInitiativesGetSustainabilityInitiativesQueryError) {
    logQueryErrorToSentry(
      sustainableInitiativesGetSustainabilityInitiativesQueryError,
      'get Sustainability Initiatives'
    )
  }

  return (
    // TODO: remove this when MUI is used globally throughout the application
    <ThemeProvider theme={THEME}>
      {renderContentToDisplay(
        !!getSustainabilityInitiativesForVenueQueryError ||
          !!sustainableInitiativesGetSustainabilityInitiativesQueryError,
        getSustainabilityInitiativesForVenueQueryData?.getSustainabilityInitiativesForVenue,
        sustainableInitiativesGetSustainabilityInitiativesQueryData?.sustainabilityInitiatives,
        dialogState
      )}
    </ThemeProvider>
  )
}
